import { useState } from "react";

import type { AdsRange } from "@/api/ads";
import { t } from "@/i18n";

interface AdsAralikProps {
  value: AdsRange;
  onChange: (range: AdsRange) => void;
}

type Sekme = 7 | 30 | 90 | "ozel";

function sekmeOf(range: AdsRange): Sekme {
  return "gun" in range ? range.gun : "ozel";
}

/**
 * Görev B: reklam aralığı seçimi hatırlansın — legacy `vdd.kpi.adsRange`
 * yerine yeni anahtar `asakai.kpi.adsRange`. AYNI anahtar altında görev A'nın
 * kaynak filtresi de saklanır (`kaynak` alanı, bkz. ReklamHunisi.tsx) — burada
 * yalnız `range` alanı okunur/yazılır (read-modify-write), `kaynak`'a
 * DOKUNULMAZ. Kalıcılık burada (AdsAralik) tutulur, `Kpi.tsx` yalnız ilk
 * `useState` değerini `readStoredAdsRange()`'den okur — böylece mount'ta
 * `/ads/ozet` YALNIZ BİR KEZ, doğru aralıkla çağrılır (onChange ile ikinci
 * bir istek TETİKLENMEZ).
 */
export const ADS_STORAGE_KEY = "asakai.kpi.adsRange";

function isValidRange(v: unknown): v is AdsRange {
  if (!v || typeof v !== "object") return false;
  const r = v as Record<string, unknown>;
  if (r.gun === 7 || r.gun === 30 || r.gun === 90) return true;
  if (typeof r.baslangic === "string" && typeof r.bitis === "string") return true;
  return false;
}

export function readStoredAdsRange(): AdsRange | null {
  try {
    const raw = window.localStorage.getItem(ADS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return isValidRange(parsed.range) ? (parsed.range as AdsRange) : null;
  } catch {
    return null;
  }
}

export function writeStoredAdsRange(range: AdsRange): void {
  try {
    const raw = window.localStorage.getItem(ADS_STORAGE_KEY);
    const cur = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    window.localStorage.setItem(ADS_STORAGE_KEY, JSON.stringify({ ...cur, range }));
  } catch {
    // yut — tercih hatirlama sadece kolaylik, yoklugu islevi bozmaz.
  }
}

/**
 * Reklam bölümünün kendi tarih aralığı seçici — legacy `#adsRangeTabs` +
 * `#adsCustomRange` portu (madde B). 7/30/90 sekmesi seçilince HEMEN
 * uygulanır; "Özel aralık" yalnız tarih girdilerini açar, "Uygula" tıklanınca
 * (ya da her iki tarih doluyken) `onChange` tetiklenir. Her iki durumda da
 * seçim `writeStoredAdsRange` ile localStorage'a yazılır (görev B).
 */
export function AdsAralik({ value, onChange }: AdsAralikProps) {
  const baslangicVarsayilan = "baslangic" in value ? value.baslangic : "";
  const bitisVarsayilan = "baslangic" in value ? value.bitis : "";
  const [sekme, setSekme] = useState<Sekme>(() => sekmeOf(value));
  const [taslakBaslangic, setTaslakBaslangic] = useState(baslangicVarsayilan);
  const [taslakBitis, setTaslakBitis] = useState(bitisVarsayilan);

  function secSekme(s: Sekme) {
    setSekme(s);
    if (s !== "ozel") {
      const range: AdsRange = { gun: s };
      onChange(range);
      writeStoredAdsRange(range);
    }
  }

  function uygula() {
    if (!taslakBaslangic || !taslakBitis) return;
    const range: AdsRange = { baslangic: taslakBaslangic, bitis: taslakBitis };
    onChange(range);
    writeStoredAdsRange(range);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="tablist" aria-label={t("kpi.aralik.baslik")} className="inline-flex gap-0.5 rounded-[9px] bg-bg p-0.5">
        {([7, 30, 90] as const).map((gun) => {
          const selected = sekme === gun;
          return (
            <button
              key={gun}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => secSekme(gun)}
              className={`min-h-[36px] rounded-[7px] px-3 text-[13px] font-semibold transition-colors ${
                selected ? "bg-card text-brand-primary shadow-sm" : "text-muted hover:text-ink"
              }`}
            >
              {t(`kpi.aralik.son${gun}`)}
            </button>
          );
        })}
        <button
          type="button"
          role="tab"
          aria-selected={sekme === "ozel"}
          onClick={() => secSekme("ozel")}
          className={`min-h-[36px] rounded-[7px] px-3 text-[13px] font-semibold transition-colors ${
            sekme === "ozel" ? "bg-card text-brand-primary shadow-sm" : "text-muted hover:text-ink"
          }`}
        >
          {t("kpi.aralik.ozel")}
        </button>
      </div>

      {sekme === "ozel" && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            aria-label={t("kpi.aralik.baslangic")}
            value={taslakBaslangic}
            onChange={(e) => setTaslakBaslangic(e.target.value)}
            className="input !w-auto"
          />
          <span className="text-xs text-muted">→</span>
          <input
            type="date"
            aria-label={t("kpi.aralik.bitis")}
            value={taslakBitis}
            onChange={(e) => setTaslakBitis(e.target.value)}
            className="input !w-auto"
          />
          <button
            type="button"
            onClick={uygula}
            disabled={!taslakBaslangic || !taslakBitis}
            className="min-h-[44px] rounded-lg border border-line bg-card px-3 text-sm font-medium text-ink disabled:opacity-50"
          >
            {t("kpi.aralik.uygula")}
          </button>
        </div>
      )}
    </div>
  );
}
